import { useNavigate } from "react-router-dom";
import { InputSelect } from "@/shared/components/form/InputSelect";
import { Button } from "@/shared/components/form/Button";
import { Loader2 } from "lucide-react";
import type { ClientAccount } from "../../clients/types/clients-api.types";
import { paymentAccountSelectLabel } from "../utils/paymentAccountSelectLabel";

export type PayerAccountsPhase =
  | "idle"
  | "no_ids"
  | "loading"
  | "ready"
  | "unavailable"
  | "error";

type SelectOption = { label: string; value: string };

type PaymentPayerAccountSectionProps = {
  pagadorId: string | null;
  phase: PayerAccountsPhase;
  accounts: ClientAccount[];
  loadErrorMessage: string | null;
  selectedAccountId: string | null;
  onSelectAccountId: (id: string | null) => void;
  disabled?: boolean;
  label: string;
  placeholder: string;
  missingAccountsMessage: string;
  loadingMessage: string;
  selectError?: string;
  /** Si se envía, se muestra junto al aviso de cuentas faltantes (misma clave que búsqueda de cliente). */
  goToClientsButtonLabel?: string;
};

export function PaymentPayerAccountSection({
  pagadorId,
  phase,
  accounts,
  loadErrorMessage,
  selectedAccountId,
  onSelectAccountId,
  disabled = false,
  label,
  placeholder,
  missingAccountsMessage,
  loadingMessage,
  selectError,
  goToClientsButtonLabel,
}: PaymentPayerAccountSectionProps) {
  const navigate = useNavigate();

  if (!pagadorId) return null;

  const showMissingBanner =
    phase === "no_ids" || phase === "unavailable";
  const showLoadErrorBanner = phase === "error" && loadErrorMessage;
  const showSelect = phase === "ready" && accounts.length > 0;

  const options: SelectOption[] = accounts.map((a) => ({
    label: paymentAccountSelectLabel(a),
    value: a.id,
  }));

  const value =
    options.find((o) => o.value === selectedAccountId) ?? null;

  return (
    <div className="space-y-3">
      {phase === "loading" && (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 py-2">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          <span>{loadingMessage}</span>
        </div>
      )}

      {showMissingBanner && (
        <div
          className="rounded-lg border border-amber-200/90 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/35 px-4 py-3 text-sm text-amber-950 dark:text-amber-100 leading-relaxed flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4"
          role="alert"
        >
          <p className="leading-relaxed flex-1 min-w-0">{missingAccountsMessage}</p>
          {goToClientsButtonLabel ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/app/clients")}
              className="shrink-0 self-start sm:self-center text-sm py-1.5 px-3 border-amber-300 dark:border-amber-700 text-amber-950 dark:text-amber-100 hover:bg-amber-100/80 dark:hover:bg-amber-900/40"
            >
              {goToClientsButtonLabel}
            </Button>
          ) : null}
        </div>
      )}

      {showLoadErrorBanner && (
        <div
          className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 px-4 py-3 text-sm text-red-800 dark:text-red-200"
          role="alert"
        >
          {loadErrorMessage}
        </div>
      )}

      {showSelect && (
        <InputSelect<SelectOption>
          name="payment-form-payer-account"
          label={label}
          options={options}
          value={value}
          onChange={(v) => {
            const opt = (Array.isArray(v) ? v[0] : v) ?? null;
            onSelectAccountId(opt?.value ?? null);
          }}
          placeholder={placeholder}
          isDisabled={disabled}
          isLoading={false}
          errorMessage={selectError}
          maxWidth="max-w-full"
          controlClassName="!h-12"
        />
      )}
    </div>
  );
}
